<template>
  <router-view v-if="isPublicPage" class="public-page" />
  <el-container v-else class="layout">
    <!-- 手机端遮罩（抽屉菜单展开时） -->
    <div v-if="isMobile && asideVisible" class="mobile-mask" @click="toggleAside"></div>
    <!-- Sidebar -->
    <el-aside :width="asideWidth" class="sidebar" :class="{ 'mobile-sidebar': isMobile }">
      <div class="logo">
        <div class="logo-brand">找客户 · AI客源引擎</div>
        <div class="logo-sub">客源有路，增长有数</div>
        <!-- 手机端：菜单栏内「收起」标识（收回到左侧） -->
        <div v-if="isMobile" class="sidebar-collapse-btn" @click="toggleAside" title="收起菜单">
          <span class="scb-icon">◀</span><span>收起菜单</span>
        </div>
      </div>
      <el-menu
        :default-active="activeMenu"
        router
        background-color="#001529"
        text-color="#94a3b8"
        active-text-color="#fff"
        @select="onMenuSelect"
      >
        <el-menu-item index="/dashboard">
          <span>📊 仪表盘</span>
        </el-menu-item>

        <el-menu-item index="/leads">
          <span>📋 线索库</span>
        </el-menu-item>

        <el-sub-menu index="grp-capture">
          <template #title>🤖 自动截流</template>
          <el-menu-item index="/monitor">🛰️ 监控截流</el-menu-item>
          <el-menu-item index="/live">📺 直播监控</el-menu-item>
        </el-sub-menu>

        <el-sub-menu index="grp-content">
          <template #title>🎬 内容制作</template>
          <el-menu-item index="/library">📥 素材库</el-menu-item>
          <el-menu-item index="/radar">📡 爆款雷达</el-menu-item>
          <el-menu-item index="/replica">🔥 爆款复刻</el-menu-item>
          <el-menu-item index="/content">✍️ 内容工厂</el-menu-item>
          <el-menu-item index="/video">🎬 H3 AI 视频</el-menu-item>
          <el-menu-item index="/benchmark">🎯 对标分解</el-menu-item>
        </el-sub-menu>

        <el-sub-menu index="grp-publish">
          <template #title>🚀 发布管理</template>
          <el-menu-item index="/quick">⚡ 快捷发布</el-menu-item>
          <el-menu-item index="/publish">📤 发布队列</el-menu-item>
          <el-menu-item index="/landing">🌉 数字名片</el-menu-item>
        </el-sub-menu>

        <el-menu-item index="/marketing">
          <span>🎯 营销军师</span>
        </el-menu-item>

        <el-menu-item index="/tasks">
          <span>⚙️ 任务管理</span>
        </el-menu-item>

        <el-sub-menu index="grp-ops">
          <template #title>🛠 运营配置</template>
          <el-menu-item v-if="userRoleTag === '管理员'" index="/merchants">🏢 商户管理</el-menu-item>
          <el-menu-item index="/accounts">👥 账号管理</el-menu-item>
          <el-menu-item index="/industry">🎯 行业配置</el-menu-item>
          <el-menu-item index="/scripts">💬 话术管理</el-menu-item>
          <el-menu-item index="/settings">🔧 系统设置</el-menu-item>
          <el-menu-item index="/help">📖 使用教程</el-menu-item>
          <el-menu-item index="/invite">🍵 邀请好友</el-menu-item>
          <el-menu-item index="/withdraw">💸 推广提现</el-menu-item>
        </el-sub-menu>

        <el-menu-item index="/recharge">
          <span>💰 找客点</span>
        </el-menu-item>
      </el-menu>
    </el-aside>

    <!-- Main content -->
    <el-container>
      <el-header class="header">
        <div class="header-left">
          <el-button class="hamburger" circle text @click="toggleAside" :title="asideVisible ? '收起菜单' : '展开菜单'">
            <span class="hamburger-icon">{{ asideVisible ? '✕' : '☰' }}</span>
          </el-button>
          <div class="header-brand">
            <span class="header-brand-top">找客户 · AI客源引擎</span>
            <span class="header-brand-sub">一套系统，搞定找客户全链路</span>
          </div>
        </div>
        <!-- 平台切换 Tab（全局状态，各页面按需过滤） -->
        <div class="platform-tabs">
          <el-radio-group v-model="currentPlatform" size="small" @change="onPlatformChange">
            <el-radio-button value="douyin">抖音</el-radio-button>
            <el-radio-button value="kuaishou">快手</el-radio-button>
            <el-radio-button value="xhs">小红书</el-radio-button>
          </el-radio-group>
        </div>
        <!-- 当前登录身份（登录状态 + 退出） -->
        <div class="header-right">
          <!-- 全局消息铃铛（P2：雷达/系统消息，未读红点 + 新消息提示音） -->
          <el-popover v-if="isLoggedIn" placement="bottom-end" :width="370" trigger="click"
                      popper-class="bell-popover" @show="onBellOpen">
            <template #reference>
              <div class="bell-wrap" title="消息中心">
                <span class="bell-icon">🔔</span>
                <span v-if="bellUnread > 0" class="bell-badge">{{ bellUnread > 99 ? '99+' : bellUnread }}</span>
              </div>
            </template>
            <div class="bell-panel">
              <div class="bell-head">
                <b>消息中心</b>
                <span class="bell-sub">{{ bellUnread > 0 ? bellUnread + ' 条未读' : '全部已读' }}</span>
                <el-button size="small" text type="primary" @click="readAllMsgs">全部已读</el-button>
              </div>
              <div v-if="!bellMsgs.length" class="bell-empty">暂无消息<br>雷达采到行业新爆款会第一时间提醒你</div>
              <div v-for="m in bellMsgs" :key="m.id" class="bell-item" :class="{ unread: !m.is_read }">
                <div class="bi-main" @click="openMsg(m)">
                  <div class="bi-title">{{ m.title }}</div>
                  <div class="bi-content">{{ m.content }}</div>
                </div>
                <div class="bi-foot">
                  <span class="bi-time">{{ m.created_at }}</span>
                  <el-button v-if="m.radar_id" size="mini" type="primary" plain
                             :loading="bellDlId === m.radar_id" @click.stop="bellReplica(m)">⚡ 一键复刻</el-button>
                </div>
              </div>
            </div>
          </el-popover>
          <el-dropdown v-if="isLoggedIn" trigger="click" @command="onUserCmd">
            <span class="user-chip">
              <span class="user-avatar-wrap">
                <span class="user-avatar">{{ avatarChar }}</span>
                <span class="user-online"></span>
              </span>
              <span class="user-meta">
                <span class="user-name">{{ displayName }}</span>
                <el-tag v-if="userRoleTag" size="small" effect="dark" class="user-role" :type="userRoleTag === '管理员' ? 'warning' : 'primary'">{{ userRoleTag }}</el-tag>
              </span>
              <span class="user-phone">{{ displayPhone }}</span>
              <span class="user-caret">▾</span>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item disabled>{{ displayName }}（{{ displayPhone }}）</el-dropdown-item>
                <el-dropdown-item divided command="logout"><span style="color:#f56c6c">退出登录</span></el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <el-button v-else text size="small" @click="$router.push('/login')" class="login-link">🔑 登录</el-button>
        </div>
      </el-header>
      <el-main class="main">
        <router-view />
      </el-main>
    </el-container>

    <!-- AI 运营助手（全局浮窗） -->
    <AIAssistant />
  </el-container>
</template>

<script>
import AIAssistant from './views/AIAssistant.vue'
import api from './api'

export default {
  name: 'App',
  components: { AIAssistant },
  data() {
    return {
      currentPlatform: localStorage.getItem('current_platform') || 'douyin',
      asideVisible: true,
      isMobile: false,
      userInfo: null,
      // 消息铃铛（P2）
      bellUnread: 0,
      bellMsgs: [],
      bellDlId: 0,
      _bellTimer: null
    }
  },
  computed: {
    activeMenu() {
      return this.$route.path
    },
    isPublicPage() {
      // 登入专题页 / 商户门禁页 / 门禁模式账号页：不渲染侧边栏与顶部菜单
      const p = this.$route.path
      if (p === '/login' || p === '/onboard') return true
      if (p === '/accounts' && this.$route.query.onboard === '1') return true
      return false
    },
    asideWidth() {
      return this.asideVisible ? '200px' : '0px'
    },
    isLoggedIn() {
      return !!localStorage.getItem('api_token')
    },
    displayName() {
      return (this.userInfo && this.userInfo.nickname) || '未登录'
    },
    displayPhone() {
      const p = (this.userInfo && this.userInfo.phone) || localStorage.getItem('user_phone') || ''
      return p || ''
    },
    userRoleTag() {
      const role = this.userInfo && this.userInfo.role
      if (role === 'admin') return '管理员'
      if (role === 'user') return '商户'
      return ''
    },
    avatarChar() {
      return (this.displayName || '用')[0]
    }
  },
  created() {
    this.onResize()
    window.addEventListener('resize', this.onResize)
    this.restoreSession()
    this.initBell()
  },
  beforeUnmount() {
    if (this._bellTimer) { clearInterval(this._bellTimer); this._bellTimer = null }
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    async restoreSession() {
      // 读取本地登录身份；有 token 时向 /auth/me 刷新最新用户信息（含 role）
      try {
        const raw = localStorage.getItem('current_user')
        this.userInfo = raw ? JSON.parse(raw) : null
      } catch (e) { this.userInfo = null }
      const token = localStorage.getItem('api_token')
      const uid = localStorage.getItem('user_id')
      if (token && !uid) {
        try {
          const r = await api.get('/auth/me')
          const u = r.user
          localStorage.setItem('user_id', String(u.id))
          localStorage.setItem('user_phone', u.phone)
          localStorage.setItem('current_user', JSON.stringify(u))
          this.userInfo = u
        } catch (e) { /* token 失效已由拦截器跳登录 */ }
      } else if (token) {
        // 已有本地身份：静默刷新一次（后端可能已变更角色等）
        try {
          const r = await api.get('/auth/me')
          const u = r.user
          if (u && u.id) {
            localStorage.setItem('current_user', JSON.stringify(u))
            localStorage.setItem('user_phone', u.phone)
            this.userInfo = u
          }
        } catch (e) { /* 忽略：网络/瞬时错误不打扰用户 */ }
      }
    },
    onResize() {
      this.isMobile = window.innerWidth < 768
      if (this.isMobile) {
        this.asideVisible = false  // 手机端默认收起侧边栏，避免挡住内容
      }
    },
    toggleAside() {
      this.asideVisible = !this.asideVisible
    },
    onMenuSelect() {
      if (this.isMobile) {
        this.asideVisible = false  // 手机端选中菜单后自动收起
      }
    },
    onUserCmd(cmd) {
      if (cmd === 'logout') this.doLogout()
    },
    doLogout() {
      // 退出登录：清除本地身份信息，回到登录页（保留平台偏好等非敏感项）
      const keep = ['current_platform', 'current_account_id']
      const keys = ['api_token', 'user_id', 'user_phone', 'current_user', 'invite_code', 'plan_id']
      keys.forEach(k => localStorage.removeItem(k))
      this.userInfo = null
      this.$router.push('/login')
      this.$message?.success?.('已退出登录')
    },
    onPlatformChange(val) {
      if (val === 'xhs') {
        // 小红书尚未上线：提示并保持当前平台（不切换、不落盘）
        this.currentPlatform = 'douyin'
        this.$message?.warning?.('抱歉！小红书功能正在建设中，敬请期待…')
        return
      }
      localStorage.setItem('current_platform', val)
      // 通知所有子页面刷新（触发全局事件）
      window.dispatchEvent(new CustomEvent('platform-changed', { detail: { platform: val } }))
    },
    // ===== 消息铃铛（P2，2026-09-04）=====
    initBell() {
      if (!Number(localStorage.getItem('user_id'))) return
      this.pollBell(true)
      this._bellTimer = setInterval(() => this.pollBell(false), 45000)
    },
    async pollBell(silent) {
      const uid = Number(localStorage.getItem('user_id'))
      if (!uid) return
      try {
        const r = await api.get('/messages/unread-count', { params: { user_id: uid } })
        const n = r.unread || 0
        if (silent) { this.bellUnread = n; return }
        if (n > this.bellUnread && n > 0) this.playBell()   // 新消息 → 提示音
        this.bellUnread = n
      } catch (e) { /* 轮询失败静默 */ }
    },
    async onBellOpen() {
      const uid = Number(localStorage.getItem('user_id'))
      if (!uid) return
      try {
        const r = await api.get('/messages', { params: { user_id: uid, limit: 30 } })
        this.bellMsgs = r.messages || []
        this.bellUnread = r.unread || 0
        if (this.bellUnread > 0) this.readAllMsgs()   // 打开即读（红点清零）
      } catch (e) { /* 静默 */ }
    },
    async readAllMsgs() {
      const uid = Number(localStorage.getItem('user_id'))
      if (!uid) return
      try {
        await api.post('/messages/read-all', null, { params: { user_id: uid } })
        this.bellUnread = 0
        this.bellMsgs = this.bellMsgs.map(m => ({ ...m, is_read: 1 }))
      } catch (e) { /* 静默 */ }
    },
    openMsg(m) {
      if (m.link_url) window.open(m.link_url, '_blank')
    },
    async bellReplica(m) {
      // 一键复刻：下载该雷达爆款 → 跳复刻页预选
      const uid = Number(localStorage.getItem('user_id'))
      if (!uid || !m.radar_id) { this.$message?.warning?.('请先登录'); return }
      this.bellDlId = m.radar_id
      try {
        await api.post('/radar/replica-prepare', {
          radar_id: m.radar_id,
          account_id: Number(localStorage.getItem('current_account_id') || 0)
        })
        this.$message?.success?.('正在下载无水印素材…完成后自动进入复刻页')
        let assetId = 0
        for (let i = 0; i < 60; i++) {
          await new Promise(r => setTimeout(r, 2000))
          try {
            const s = await api.get('/radar/replica-status/' + m.radar_id)
            if (s.status === 'done') { assetId = s.asset_id; break }
            if (s.status === 'failed') break
          } catch (e) { /* continue */ }
        }
        if (assetId) {
          this.$router.push('/replica?source_asset_id=' + assetId)
        } else {
          this.$message?.error?.('素材下载失败，请稍后到爆款雷达重试')
        }
      } catch (e) {
        this.$message?.error?.(e.message || '操作失败')
      } finally {
        this.bellDlId = 0
      }
    },
    playBell() {
      // WebAudio 合成短提示音（叮~），无音频资源依赖
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext
        if (!Ctx) return
        const ctx = this._bellCtx || (this._bellCtx = new Ctx())
        if (ctx.state === 'suspended') ctx.resume()
        const t0 = ctx.currentTime
        const mk = (freq, at, dur, vol) => {
          const o = ctx.createOscillator(), g = ctx.createGain()
          o.type = 'sine'; o.frequency.value = freq
          g.gain.setValueAtTime(0.0001, at)
          g.gain.exponentialRampToValueAtTime(vol, at + 0.02)
          g.gain.exponentialRampToValueAtTime(0.0001, at + dur)
          o.connect(g); g.connect(ctx.destination)
          o.start(at); o.stop(at + dur + 0.05)
        }
        mk(1046, t0, 0.22, 0.28)      // C6
        mk(1568, t0 + 0.16, 0.28, 0.22) // G6（叮~咚）
      } catch (e) { /* 音频被禁则静默 */ }
    }
  }
}
</script>

<style>
/* ===== 科技主题：黑 / 蓝 / 绿 / 白（2026-09-01） ===== */
:root {
  --el-color-primary: #2563EB;
  --el-color-primary-light-3: #3b82f6;
  --el-color-primary-light-5: #60a5fa;
  --el-color-primary-light-7: #93c5fd;
  --el-color-primary-light-8: #bfdbfe;
  --el-color-primary-light-9: #dbeafe;
  --el-color-primary-dark-2: #1e40af;
  --el-color-success: #22c55e;
  --el-color-success-light-3: #4ade80;
  --el-color-success-light-5: #86efac;
  --el-color-success-light-7: #bbf7d0;
  --el-color-success-light-8: #dcfce7;
  --el-color-success-light-9: #f0fdf4;
  --el-color-success-dark-2: #16a34a;
  --el-color-warning: #f59e0b;
  --el-color-warning-light-3: #fbbf24;
  --el-color-warning-light-5: #fcd34d;
  --el-color-warning-light-7: #fde68a;
  --el-color-warning-light-8: #fef3c7;
  --el-color-warning-light-9: #fffbeb;
  --el-color-warning-dark-2: #d97706;
  --brand-blue: #2563EB;
  --brand-cyan: #38bdf8;
  --brand-green: #22c55e;
  --brand-dark: #070B14;
}

/* ===== 全站暗黑布局（阿里山式深色 UI） ===== */
html.dark {
  --el-bg-color: #0e1526;
  --el-bg-color-page: #0a0f1a;
  --el-bg-color-overlay: #131c30;
  --el-fill-color: #1a2438;
  --el-fill-color-light: #16203a;
  --el-fill-color-lighter: #131c30;
  --el-fill-color-blank: #0e1526;
  --el-border-color: rgba(148,163,184,.18);
  --el-border-color-light: rgba(148,163,184,.14);
  --el-border-color-lighter: rgba(148,163,184,.1);
  --el-border-color-extra-light: rgba(148,163,184,.08);
  --el-text-color-primary: #e2e8f0;
  --el-text-color-regular: #cbd5e1;
  --el-text-color-secondary: #94a3b8;
  --el-text-color-placeholder: #64748b;
  --el-mask-color: rgba(2,6,23,.75);
  --el-box-shadow: 0 8px 24px rgba(0,0,0,.45);
  --el-box-shadow-light: 0 4px 14px rgba(0,0,0,.35);
}
html.dark body { background: #0a0f1a; color: #e2e8f0; }
html.dark .main { background: #0a0f1a !important; }
html.dark .header { background: #0e1526 !important; border-image: linear-gradient(90deg, #2563EB, #38bdf8, #22c55e) 1; }
html.dark .header-brand-top { color: #f1f5f9; }
html.dark .hamburger { color: #cbd5e1; }
html.dark .el-card { background: #0e1526; border-color: rgba(148,163,184,.14); color: #e2e8f0; }
html.dark .el-table { background: transparent; --el-table-tr-bg-color: transparent; --el-table-header-bg-color: #131c30; --el-table-border-color: rgba(148,163,184,.12); color: #cbd5e1; }
html.dark .el-table th.el-table__cell { background: #131c30; color: #94a3b8; }
html.dark .el-tabs__item { color: #94a3b8; }
html.dark .el-tabs__item.is-active { color: #38bdf8; }
html.dark .el-dialog { background: #0e1526; }
html.dark .el-drawer { background: #0e1526; }
html.dark .el-message-box { background: #0e1526; }
html.dark .el-popper.is-light { background: #131c30 !important; border-color: rgba(148,163,184,.15) !important; }
html.dark .el-select-dropdown { background: #131c30 !important; }
html.dark .el-select-dropdown__item { color: #cbd5e1; }
html.dark .el-select-dropdown__item.is-hovering { background: rgba(37,99,235,.18); }
html.dark .el-pagination { color: #94a3b8; }
html.dark .el-empty__description p { color: #94a3b8; }
html.dark .el-form-item__label { color: #cbd5e1; }
html.dark .el-radio__label, html.dark .el-checkbox__label { color: #cbd5e1; }
html.dark .el-page-header__content { color: #e2e8f0; }
html.dark .page-title, html.dark .section-title { color: #f1f5f9; }

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body, #app {
  height: 100%;
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Microsoft YaHei', Arial, sans-serif;
}

.public-page {
  display: block;
  min-height: 100vh;
}

.layout {
  height: 100%;
}

.sidebar {
  background-color: #070B14;
  overflow: hidden;
  transition: width 0.25s ease;
  flex-shrink: 0;
}

.sidebar .logo {
  color: #fff;
  text-align: center;
  padding: 16px 8px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  line-height: 1.4;
}

.sidebar .logo .logo-brand {
  font-size: 17px;
  font-weight: bold;
  letter-spacing: 1px;
}

.sidebar .logo .logo-sub {
  font-size: 12px;
  color: #38bdf8;
  margin-top: 4px;
}

/* 手机端侧边栏内「收起」按钮（仅移动端渲染） */
.sidebar-collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  padding: 9px 0;
  color: #c9d1d9;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.15s;
  user-select: none;
}
.sidebar-collapse-btn:active {
  background: rgba(255, 255, 255, 0.15);
}
.scb-icon {
  font-size: 12px;
}

.sidebar .el-menu {
  border-right: none;
  /* 菜单超高时内部滚动（修复手机端「运营配置」子菜单/底部菜单项显示不全） */
  max-height: calc(100vh - 96px);
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
}
.sidebar .el-menu::-webkit-scrollbar { width: 4px; }
.sidebar .el-menu::-webkit-scrollbar-thumb { background: rgba(148,163,184,.35); border-radius: 4px; }

/* 子菜单分组样式（深色背景适配） */
.sidebar .el-menu .el-sub-menu__title {
  font-size: 16px;
  height: 48px;
  line-height: 48px;
  border-radius: 6px;
  transition: all 0.25s ease;
}

.sidebar .el-menu .el-sub-menu__title:hover {
  background-color: #2563EB !important;
  color: #fff !important;
}

.sidebar .el-menu .el-sub-menu .el-menu-item {
  font-size: 14px;
  height: 42px;
  line-height: 42px;
  padding-left: 40px !important;
  background-color: #05070d;
}

.sidebar .el-menu .el-sub-menu .el-menu-item:hover {
  background-color: #2563EB !important;
  color: #fff !important;
  padding-left: 48px !important;
}

.sidebar .el-menu .el-sub-menu .el-menu-item.is-active {
  background-color: #2563EB !important;
  color: #fff !important;
  font-weight: 600;
}

/* 菜单项：字体调大一号 + 悬停色块光标 */
.sidebar .el-menu .el-menu-item {
  font-size: 16px;
  height: 48px;
  line-height: 48px;
  margin: 2px 0;
  border-radius: 6px;
  transition: all 0.25s ease;
}

.sidebar .el-menu .el-menu-item:hover {
  background-color: #2563EB !important;   /* 悬停色块 */
  color: #fff !important;
  padding-left: 24px !important;          /* 光标前移效果 */
}

.sidebar .el-menu .el-menu-item.is-active {
  background-color: #2563EB !important;
  color: #fff !important;
  font-weight: 600;
}

.header {
  background: #fff;
  border-bottom: 2px solid transparent;
  border-image: linear-gradient(90deg, #2563EB, #38bdf8, #22c55e) 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hamburger {
  font-size: 20px;
  color: #333;
  flex-shrink: 0;
}

.hamburger-icon {
  display: inline-block;
  font-size: 20px;
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.platform-tabs {
  margin-right: 8px;
}

.header-right {
  display: flex;
  align-items: center;
  margin-right: 6px;
  flex-shrink: 0;
}

.user-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 20px;
  background: rgba(37, 99, 235, .08);
  border: 1px solid rgba(37, 99, 235, .25);
  transition: background .2s;
  outline: none;
}
.user-chip:hover { background: rgba(37, 99, 235, .16); }
html.dark .user-chip { background: rgba(37, 99, 235, .12); border-color: rgba(56, 189, 248, .25); }
html.dark .user-chip:hover { background: rgba(37, 99, 235, .22); }

.user-avatar-wrap {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}

.user-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563EB, #38bdf8);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 在线绿点：表示当前已登录后台账号 */
.user-online {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #22c55e;
  border: 2px solid #fff;
  box-sizing: content-box;
  box-shadow: 0 0 4px rgba(34, 197, 94, .7);
}
html.dark .user-online { border-color: #0e1526; }

.user-meta { display: flex; align-items: center; gap: 6px; }

.user-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
html.dark .user-name { color: #e2e8f0; }

.user-role { line-height: 1.2 !important; }

.user-phone {
  font-size: 12px;
  color: #64748b;
  font-variant-numeric: tabular-nums;
}
html.dark .user-phone { color: #94a3b8; }

.user-caret { font-size: 10px; color: #94a3b8; }

.header-brand-top {
  font-size: 19px;
  font-weight: 900;
  color: #fff;
  letter-spacing: 1px;
}

.header-brand-sub {
  font-size: 13px;
  color: #e2e8f0;
  font-weight: 700;
}

.main {
  background: #f5f7fa;
}

/* ===== 手机端适配 ===== */
@media (max-width: 767px) {
  .header-brand-top {
    font-size: 16px;
    letter-spacing: 0px;
  }
  .header-brand-sub {
    display: none;  /* 手机端隐藏副标题，省空间 */
  }
  .platform-tabs .el-radio-button__inner {
    font-size: 12px;
    padding: 8px 10px;
  }
  /* 手机端：用户区收窄，只留头像（隐藏昵称/角色/手机号省空间） */
  .user-chip { padding: 3px 6px; gap: 0; border-radius: 50%; background: transparent; border-color: transparent; }
  .user-avatar { width: 24px; height: 24px; font-size: 12px; }
  .user-meta, .user-phone, .user-caret { display: none; }
  .login-link { font-size: 12px; }
  .sidebar .logo {
    padding: 10px 4px;
  }
  .sidebar .logo .logo-brand {
    font-size: 15px;
  }
  .sidebar .logo .logo-sub {
    font-size: 10px;
  }
  /* 手机端侧边栏 = 抽屉浮层（覆盖内容，不挤压） */
  .sidebar.mobile-sidebar {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 2000;
    box-shadow: 2px 0 12px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
  }
  .sidebar.mobile-sidebar .el-menu {
    flex: 1;
    max-height: none;   /* 抽屉内自然滚动 */
    overflow-y: auto;
  }
  .mobile-mask {
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1999;
  }
  /* 手机端主内容 padding 适配 */
  .main {
    padding: 10px !important;
  }
  /* ===== 手机端文字放大一号（正文/内容，标题提示词除外） ===== */
  body { font-size: 16px; }
  .el-card__body { padding: 16px; font-size: 15px; }
  .el-card__header { font-size: 16px; padding: 14px 16px; text-align: center; }
  .el-table { font-size: 14px; }
  .el-table .el-button--small { font-size: 13px; }
  .el-form-item__label { font-size: 15px; }
  .el-form-item__content { font-size: 15px; }
  .el-tabs__item { font-size: 15px; }
  .el-drawer__body { font-size: 15px; padding: 14px; }
  .el-dialog__body { font-size: 15px; }
  .el-dialog__title { font-size: 17px; }
  .el-message-box__message { font-size: 15px; }
  .el-message { font-size: 15px; }
  .el-empty__description { font-size: 14px; }
  /* ===== 手机端输入框/按钮/框框放大 ===== */
  .el-input__wrapper { min-height: 44px; padding: 1px 14px; }
  .el-input__inner { height: 42px !important; line-height: 42px !important; font-size: 16px !important; }
  .el-textarea__inner { font-size: 16px !important; padding: 10px 14px; line-height: 1.6; }
  .el-select__wrapper { min-height: 44px; font-size: 16px; }
  .el-button { height: 40px; padding: 0 18px; font-size: 15px; }
  .el-button--small { height: 32px; padding: 0 12px; font-size: 14px; }
  .el-radio-button__inner { font-size: 14px; padding: 10px 14px; }
  .el-radio__label { font-size: 15px; }
  .el-checkbox__label { font-size: 15px; }
  .el-switch__label { font-size: 15px; }
  .el-form-item { margin-bottom: 18px; }
  .el-card { border-radius: 10px; }
  .el-dialog { width: 92% !important; }
  .el-pagination { font-size: 14px; }
  .el-pagination button, .el-pagination .el-pager li { min-width: 32px; height: 32px; }
  .el-tag { font-size: 13px; height: 26px; padding: 0 10px; line-height: 24px; }
  .el-alert { padding: 12px 14px; font-size: 14px; }
  .el-alert__title { font-size: 14px; }
  .el-statistic__content { font-size: 22px; }
  .el-statistic__title { font-size: 14px; }
  .el-progress__text { font-size: 14px; }
  /* 标题文字（正文排除后统一居中视觉） */
  .page-title, .section-title { text-align: center; }
}

/* ===== 移动端全局防横向溢出兜底（2026-09-04：手机端录屏确认可左右滑动，全站根治） ===== */
@media (max-width: 768px) {
  html, body { max-width: 100%; overflow-x: hidden !important; }
  #app, .layout, .el-container, .el-container .el-main, .el-header { max-width: 100%; overflow-x: hidden; }
  .el-card, .el-card__body, .el-form, .el-form-item, .el-form-item__content,
  .el-row, .el-col, .el-tabs, .el-tabs__content, .el-table, .el-dialog, .el-drawer { max-width: 100% !important; overflow-x: hidden; }
  .el-form-item__content { flex-wrap: wrap; }
  .el-radio-group, .el-checkbox-group { display: flex !important; flex-wrap: wrap !important; max-width: 100%; }
  .el-radio-button { flex-shrink: 1; }
  .el-input, .el-textarea, .el-select, .el-select__wrapper, .el-slider, .el-autocomplete { max-width: 100% !important; }
  .el-upload, .el-button { max-width: 100%; }
  img, video { max-width: 100% !important; height: auto; }
  .el-dialog { width: calc(100vw - 24px) !important; margin: 6vh auto 0; }
  .el-message-box { width: calc(100vw - 48px) !important; max-width: 100%; }
  .row, .slot-row, .header-left, .header-right, .platform-tabs, .el-header, .head-actions { flex-wrap: wrap !important; }
  video { max-height: 42vh; }
  /* 批次34：手机 header 两行式，防铃铛/账号被内容遮盖 */
  .el-header.header {
    height: auto !important;
    min-height: 54px;
    position: relative;
    z-index: 60;
    padding: 8px 6px;
    gap: 6px;
    align-items: center;
  }
  .header .header-left { flex: 1 1 auto; min-width: 0; }
  .header .header-brand { gap: 8px; min-width: 0; }
  .header-brand-top { font-size: 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .header-brand-sub { display: none; }
  .header .platform-tabs {
    order: 3;
    width: 100%;
    justify-content: center;
    margin: 2px 0 0;
  }
  .header .header-right { order: 2; flex-shrink: 0; margin-left: auto; }
}

/* ===== 全局消息铃铛（P2 2026-09-04）===== */
.header-right { display: flex; align-items: center; gap: 10px; }
.bell-wrap { position: relative; cursor: pointer; padding: 6px 4px; display: flex; align-items: center; }
.bell-icon { font-size: 20px; line-height: 1; filter: drop-shadow(0 0 6px rgba(56,189,248,.5)); }
.bell-badge {
  position: absolute; top: 0; right: -6px; min-width: 16px; height: 16px; border-radius: 8px;
  background: #f56c6c; color: #fff; font-size: 10px; line-height: 16px; text-align: center;
  padding: 0 4px; font-weight: 700; box-shadow: 0 0 6px rgba(245,108,108,.7);
}
.bell-panel { max-height: 400px; overflow-y: auto; }
.bell-head { display: flex; align-items: center; gap: 8px; padding-bottom: 10px; border-bottom: 1px solid rgba(148,163,184,.2); }
.bell-head b { font-size: 15px; }
.bell-sub { flex: 1; font-size: 12px; color: #94a3b8; }
.bell-empty { text-align: center; color: #94a3b8; font-size: 13px; padding: 26px 8px; line-height: 1.9; }
.bell-item { padding: 10px 2px; border-bottom: 1px dashed rgba(148,163,184,.15); cursor: pointer; }
.bell-item:last-child { border-bottom: none; }
.bell-item.unread { background: rgba(56,189,248,.05); border-radius: 8px; padding: 10px 8px; }
.bi-title { font-size: 13.5px; font-weight: 600; color: var(--el-text-color-primary, #1f2937); line-height: 1.5; }
.bi-content { font-size: 12.5px; color: var(--el-text-color-secondary, #606266); margin-top: 4px; line-height: 1.6; }
.bi-foot { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
.bi-time { font-size: 11.5px; color: var(--el-text-color-placeholder, #909399); flex: 1; }
@media (max-width: 480px) {
  .bell-icon { font-size: 18px; }
}
</style>
